/**
 * GpsAlarmas — eventos críticos detectados por los equipos GPS (mockup).
 *
 * Lista filtrable por severidad + toggle "incluir resueltas". Cada item enlaza
 * de vuelta al monitor en vivo (`?device=ID`).  Antes "alertas" en la new front,
 * acá lo dejamos como "alarmas" por preferencia del usuario.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, BatteryWarning, Bell, ChevronRight, Gauge,
  MapPin, ShieldAlert, Siren, Timer,
} from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { EmptyState } from '../../../components/ui/EmptyState';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Skeleton } from '../../../components/ui/Skeleton';
import {
  alarmaLabel, formatDateTime, formatRelative, gpsApi, severidadLabel, severidadTone,
} from './gpsMock';

// Orden canónico de tipos (sigue alarmaLabel) para los chips de filtro;
// solo se muestran los tipos presentes en los datos cargados.
const TIPO_ORDER = Object.keys(alarmaLabel);

const tipoIcon = {
  exceso_velocidad:     <Gauge          className="w-4 h-4" />,
  geocerca_entrada:     <MapPin         className="w-4 h-4" />,
  geocerca_salida:      <MapPin         className="w-4 h-4" />,
  bateria_desconectada: <BatteryWarning className="w-4 h-4" />,
  panico:               <Siren          className="w-4 h-4" />,
  frenada_brusca:       <AlertTriangle  className="w-4 h-4" />,
  aceleracion_brusca:   <AlertTriangle  className="w-4 h-4" />,
  parada_prolongada:    <Timer          className="w-4 h-4" />,
  sin_senal:            <ShieldAlert    className="w-4 h-4" />,
  mantenimiento:        <Bell           className="w-4 h-4" />,
};

export default function GpsAlarmas() {
  const { t } = useTranslation();
  const [alarmas, setAlarmas] = useState(null);
  const [sevFilter, setSevFilter] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [showResolved, setShowResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // TODO(back): cablear cuando el back exponga /alarmas.
    gpsApi.alarmas().then((a) => { if (!cancelled) setAlarmas(a); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!alarmas) return [];
    let arr = alarmas;
    if (!showResolved) arr = arr.filter((a) => !a.resuelta);
    if (sevFilter) arr = arr.filter((a) => a.severidad === sevFilter);
    if (tipoFilter) arr = arr.filter((a) => a.tipo === tipoFilter);
    return [...arr].sort((a, b) => +new Date(b.at) - +new Date(a.at));
  }, [alarmas, sevFilter, tipoFilter, showResolved]);

  // Tipos presentes en los datos (no inventamos categorías vacías); el conteo
  // respeta el toggle "incluir resueltas" para que los chips reflejen la lista.
  const tipoChips = useMemo(() => {
    const base = (alarmas ?? []).filter((a) => showResolved || !a.resuelta);
    const counts = base.reduce((acc, a) => {
      acc[a.tipo] = (acc[a.tipo] ?? 0) + 1;
      return acc;
    }, {});
    const present = new Set((alarmas ?? []).map((a) => a.tipo));
    return TIPO_ORDER.filter((tp) => present.has(tp)).map((tp) => ({ tipo: tp, count: counts[tp] ?? 0 }));
  }, [alarmas, showResolved]);

  const kpis = useMemo(() => {
    const all = alarmas ?? [];
    return {
      activas:   all.filter((a) => !a.resuelta).length,
      criticas:  all.filter((a) => !a.resuelta && a.severidad === 'critica').length,
      hoy:       all.length,
      resueltas: all.filter((a) => a.resuelta).length,
    };
  }, [alarmas]);

  return (
    <div className="w-full min-w-0 p-4 md:p-6 space-y-5">
      <PageHeader
        title={t("Alarmas GPS")}
        subtitle={t("Eventos críticos detectados por los equipos. Cada alarma enlaza al monitor en vivo.")}
        actions={
          <Button variant="outline" iconLeft={<Bell className="w-4 h-4" />}>
            {t("Reglas de alarma")}
          </Button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label={t("Activas")}   value={kpis.activas}   icon={<AlertTriangle />} tone={kpis.activas > 0 ? 'warning' : 'neutral'} />
        <Kpi label={t("Críticas")}  value={kpis.criticas}  icon={<Siren />}         tone={kpis.criticas > 0 ? 'error' : 'neutral'} />
        <Kpi label={t("Total hoy")} value={kpis.hoy}       icon={<Bell />}          tone="info" />
        <Kpi label={t("Resueltas")} value={kpis.resueltas} icon={<ShieldAlert />}   tone="success" />
      </div>

      <div className="space-y-2.5">
        <div className="flex flex-wrap items-center gap-2">
          {/* flex-wrap: con labels más largos (i18n) los pills no caben en 390px. */}
          <div className="flex flex-wrap items-center gap-1 bg-bg border border-border rounded-lg p-1">
            {[['', t('Todas')], ['critica', t('Críticas')], ['alta', t('Altas')], ['media', t('Medias')], ['info', t('Info')]].map(([v, l]) => (
              <button
                key={v || 'all'}
                onClick={() => setSevFilter(v)}
                className={cn(
                  'px-3 h-8 text-xs font-medium rounded-md transition-colors',
                  sevFilter === v ? 'bg-brand-500 text-white' : 'text-fg-muted hover:text-fg',
                )}
              >
                {l}
              </button>
            ))}
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-fg-muted ml-auto cursor-pointer">
            <input
              type="checkbox"
              className="rounded border-border text-brand-500 focus:ring-brand-500"
              checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)}
            />
            {t("Incluir resueltas")}
          </label>
        </div>

        {/* Filtro por tipo de alarma (pánico, exceso, sin señal…). Chips
            derivados de los tipos presentes en los datos. En mobile van en una
            sola fila scrolleable (con ~10 tipos el wrap empuja el listado
            fuera del primer viewport); en sm+ vuelven a flex-wrap. */}
        {tipoChips.length > 0 && (
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-x-visible sm:pb-0">
            <span className="shrink-0 text-[11px] uppercase tracking-wide font-medium text-fg-subtle mr-0.5">{t("Tipo")}</span>
            <button
              onClick={() => setTipoFilter('')}
              aria-pressed={tipoFilter === ''}
              className={cn(
                'shrink-0 inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium rounded-md border transition-colors',
                tipoFilter === ''
                  ? 'bg-brand-500 border-brand-500 text-white'
                  : 'bg-bg border-border text-fg-muted hover:text-fg',
              )}
            >
              {t("Todos")}
            </button>
            {tipoChips.map(({ tipo, count }) => {
              const active = tipoFilter === tipo;
              return (
                <button
                  key={tipo}
                  onClick={() => setTipoFilter(active ? '' : tipo)}
                  aria-pressed={active}
                  className={cn(
                    'shrink-0 inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium rounded-md border transition-colors',
                    active
                      ? 'bg-brand-500 border-brand-500 text-white'
                      : 'bg-bg border-border text-fg-muted hover:text-fg',
                  )}
                >
                  <span className={cn('shrink-0 [&>svg]:w-3.5 [&>svg]:h-3.5', !active && 'text-fg-subtle')} aria-hidden>
                    {tipoIcon[tipo]}
                  </span>
                  {t(alarmaLabel[tipo])}
                  <span className={cn('tabular-nums text-[10px] px-1 rounded', active ? 'bg-white/20' : 'bg-bg-muted')}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <Card padding="none" className="overflow-hidden">
        {alarmas === null ? (
          // Espeja la fila real: ícono cuadrado + título/badges + mensaje + meta,
          // y un enlace a la derecha. Antes barras planas (forma distinta → salto).
          <ul className="divide-y divide-border">
            {Array.from({ length: 5 }).map((_, i) => (
              <li key={i} className="px-5 py-4 flex items-start gap-4">
                <Skeleton className="w-10 h-10 rounded-lg shrink-0" />
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-4 w-16 rounded-full" />
                  </div>
                  <Skeleton className="h-3 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
                <Skeleton className="self-center h-3 w-20" />
              </li>
            ))}
          </ul>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<ShieldAlert />}
            title={t("Sin alarmas")}
            description={t("No hay alarmas que coincidan con los filtros. Tu flota está tranquila.")}
          />
        ) : (
          <ul className="divide-y divide-border">
            {filtered.map((a) => (
              // En mobile el link lateral dejaba ~130px para el contenido
              // (wrap palabra por palabra): el contenido va a ancho completo y
              // "Ir al monitor" baja como acción al pie; en sm+ vuelve al lado.
              <li
                key={a.id}
                className={cn(
                  'px-4 sm:px-5 py-4 hover:bg-bg-muted/60 transition-colors',
                  a.resuelta && 'opacity-60',
                )}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div
                    className={cn(
                      'w-10 h-10 rounded-lg flex items-center justify-center shrink-0',
                      a.severidad === 'critica' ? 'bg-status-error-soft text-status-error' :
                      a.severidad === 'alta'    ? 'bg-status-warning-soft text-status-warning' :
                      a.severidad === 'media'   ? 'bg-status-info-soft text-status-info' :
                                                  'bg-bg-muted text-fg-muted',
                    )}
                  >
                    {tipoIcon[a.tipo]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-fg">{t(alarmaLabel[a.tipo])}</span>
                      <Badge tone={severidadTone[a.severidad]} dot>{t(severidadLabel[a.severidad])}</Badge>
                      {a.valor && (
                        <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-bg-muted text-fg-muted">{a.valor}</span>
                      )}
                      {a.resuelta && <Badge tone="success">{t("Resuelta")}</Badge>}
                    </div>
                    <p className="text-sm text-fg-muted mt-1">{a.mensaje}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-fg-subtle">
                      {/* inline-flex + items-center: el piso táctil de 44px en <a>
                          descuadra la patente respecto del resto de la línea. */}
                      <Link to={`/admin/gps-monitoreo?device=${a.deviceId}`} className="inline-flex items-center font-mono font-semibold text-fg-muted hover:text-brand-700">
                        {a.patente}
                      </Link>
                      <span>· {a.alias}</span>
                      <span>· {a.direccion}</span>
                      <span className="tabular-nums">· {formatRelative(a.at)} ({formatDateTime(a.at)})</span>
                    </div>
                  </div>
                  <Link
                    to={`/admin/gps-monitoreo?device=${a.deviceId}`}
                    className="hidden sm:inline-flex self-center items-center gap-1 text-xs font-medium text-brand-700 hover:underline"
                  >
                    {t("Ir al monitor")} <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>
                <Link
                  to={`/admin/gps-monitoreo?device=${a.deviceId}`}
                  className="sm:hidden mt-3 flex h-11 w-full items-center justify-center gap-1 rounded-lg border border-border bg-bg text-xs font-medium text-brand-700"
                >
                  {t("Ir al monitor")} <ChevronRight className="w-3 h-3" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// KPI card local — el "Stat" del design system existente tiene otra forma
// (sin icon/tone). Mantengo este formato inline como en la fuente.
function Kpi({ label, value, icon, tone = 'neutral' }) {
  const toneCls = {
    neutral: 'bg-status-neutral-soft text-fg-muted',
    info:    'bg-status-info-soft text-status-info',
    success: 'bg-status-success-soft text-status-success',
    warning: 'bg-status-warning-soft text-status-warning',
    error:   'bg-status-error-soft text-status-error',
  }[tone] ?? 'bg-status-neutral-soft text-fg-muted';
  return (
    <div className="bg-bg border border-border rounded-xl shadow-card p-3 flex items-center gap-3">
      <span className={cn('w-9 h-9 rounded-lg flex items-center justify-center shrink-0', toneCls, '[&>svg]:w-4 [&>svg]:h-4')}>
        {icon}
      </span>
      <div className="min-w-0">
        <div className="text-xl font-semibold text-fg tabular-nums leading-none">{value}</div>
        <div className="text-[11px] text-fg-muted mt-0.5">{label}</div>
      </div>
    </div>
  );
}
